import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const occasions = [
  'Birthday',
  'Wedding',
  'Baby Shower',
  'Anniversary',
  'Graduation',
  'Corporate',
  'Other'
];

const flavors = [
  'Vanilla',
  'Chocolate',
  'Red Velvet',
  'Carrot',
  'Coffee',
  'Strawberry',
  'Lemon',
  'Custom'
];

const shapes = [
  'Round',
  'Square',
  'Heart',
  'Custom Shape'
];

const tiers = [
  'Single Tier',
  'Double Tier',
  'Triple Tier',
  'Custom'
];

const sizes = [
  '0.5 KG (4-6 servings)',
  '1 KG (8-10 servings)',
  '2 KG (15-20 servings)',
  '3 KG (25-30 servings)',
  'Custom Size'
];

const addons = [
  'Fresh Flowers',
  'Sugar Flowers',
  '3D Toppers',
  'Chocolate Decorations',
  'Fondant Figures',
  'Custom Message',
  'Special Effects'
];

const WHATSAPP_NUMBER = '94778590241';

const formatDate = (date) => (date ? date.toISOString().split('T')[0] : 'Not specified');

const toSafeId = (text) =>
  text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

export default function CakeDesign({ initialCakeType = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    cakeType: initialCakeType,
    occasion: '',
    flavor: '',
    dateNeeded: null,
    instructions: '',
    shape: '',
    tiers: '',
    size: '',
    color: '',
    addons: []
  });
  useEffect(() => {
    setFormData((prev) => ({ ...prev, cakeType: initialCakeType }));
  }, [initialCakeType]);


  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dateNeeded: date }));
  };

  const handleAddonChange = (addon) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addon)
        ? prev.addons.filter(a => a !== addon)
        : [...prev.addons, addon]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.dateNeeded) {
      alert('Please select a date for your cake.');
      return;
    }

    setIsSubmitting(true);

    const message = `Hello! I'd like to order a custom cake:
Name: ${formData.name}
Cake Type: ${formData.cakeType || 'Not specified'}
Occasion: ${formData.occasion}
Flavor: ${formData.flavor || 'Not specified'}
Shape: ${formData.shape || 'Not specified'}
Tiers: ${formData.tiers || 'Not specified'}
Size: ${formData.size || 'Not specified'}
Color Theme: ${formData.color || 'Not specified'}
Add-ons: ${formData.addons.length > 0 ? formData.addons.join(', ') : 'None'}
Date Needed: ${formatDate(formData.dateNeeded)}
Special Instructions: ${formData.instructions || 'None'}`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
    
    // Reset form after a short delay
    setTimeout(() => {
      setIsSubmitting(false);
    }, 1200);
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 to-amber-50">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header with decorative elements */}
        <div className="bg-pink-600 py-4 px-6">
          <div className="flex items-center justify-center">
            <svg className="w-8 h-8 text-white mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
            </svg>
            <h1 className="text-3xl font-bold text-white text-center font-serif">
              Custom Cake Order Form
            </h1>
          </div>
          <p className="text-pink-100 text-center mt-1">
            Design your dream cake with our cake fairy
          </p>
        </div>
        
        <div className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 border-b border-pink-200 pb-2">
                Personal Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name <span className="text-pink-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="occasion" className="block text-sm font-medium text-gray-700 mb-1">
                    Occasion <span className="text-pink-600">*</span>
                  </label>
                  <select
                    id="occasion"
                    name="occasion"
                    value={formData.occasion}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                    required
                  >
                    <option value="">Select an occasion</option>
                    {occasions.map(occasion => (
                      <option key={occasion} value={occasion}>{occasion}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="cakeType" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Cake Type
                  </label>
                  <input
                    type="text"
                    id="cakeType"
                    name="cakeType"
                    value={formData.cakeType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                    placeholder="e.g., Chocolate Cake"
                  />
                  {initialCakeType && (
                    <p className="text-xs text-pink-600 mt-1">Preselected: {initialCakeType}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Cake Details Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 border-b border-pink-200 pb-2">
                Cake Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="flavor" className="block text-sm font-medium text-gray-700 mb-1">
                    Cake Flavor
                  </label>
                  <select
                    id="flavor"
                    name="flavor"
                    value={formData.flavor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                  >
                    <option value="">Select a flavor</option>
                    {flavors.map(flavor => (
                      <option key={flavor} value={flavor}>{flavor}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="shape" className="block text-sm font-medium text-gray-700 mb-1">
                    Cake Shape
                  </label>
                  <select
                    id="shape"
                    name="shape"
                    value={formData.shape}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                  >
                    <option value="">Select a shape</option>
                    {shapes.map(shape => (
                      <option key={shape} value={shape}>{shape}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="tiers" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Tiers
                  </label>
                  <select
                    id="tiers"
                    name="tiers"
                    value={formData.tiers}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                  >
                    <option value="">Select tiers</option>
                    {tiers.map(tier => (
                      <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1">
                    Cake Size
                  </label>
                  <select
                    id="size"
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                  >
                    <option value="">Select size</option>
                    {sizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                    Color Theme
                  </label>
                  <input
                    type="text"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                    placeholder="e.g., Pink and Gold"
                  />
                </div>

                <div>
                  <label htmlFor="dateNeeded" className="block text-sm font-medium text-gray-700 mb-1">
                    Date Needed
                  </label>
                  <DatePicker
                    selected={formData.dateNeeded}
                    onChange={handleDateChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition"
                    minDate={new Date()}
                    placeholderText="Select a date"
                    dateFormat="MMMM d, yyyy"
                  />
                </div>
              </div>
            </div>

            {/* Add-ons Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 border-b border-pink-200 pb-2">
                Cake Add-ons
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {addons.map((addon) => {
                  const safeId = toSafeId(addon);
                  return (
                    <div key={addon} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`addon-${safeId}`}
                        checked={formData.addons.includes(addon)}
                        onChange={() => handleAddonChange(addon)}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`addon-${safeId}`} className="ml-2 text-sm text-gray-700">
                        {addon}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Special Instructions */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 border-b border-pink-200 pb-2">
                Special Instructions
              </h2>
              
              <div>
                <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-1">
                  Tell us about your dream cake
                </label>
                <textarea
                  id="instructions"
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition h-32"
                  placeholder="Any specific designs, themes, or special requests..."
                />
              </div>
            </div>

            {/* Submission Guidance */}
            <div className="bg-pink-50 p-4 rounded-lg border border-pink-100">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center">
                <svg className="w-5 h-5 text-pink-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How to Complete Your Order
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm">
                <li>Fill out all required fields (marked with *)</li>
                <li>Provide as much detail as possible about your cake</li>
                <li>Click "Submit Cake Order" to open WhatsApp</li>
                <li>Send any reference images directly in the WhatsApp chat</li>
                <li>Our cake artist will confirm your order details</li>
              </ol>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white flex items-center justify-center space-x-2 transition ${isSubmitting ? 'bg-pink-400' : 'bg-pink-600 hover:bg-pink-700'}`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <span>Send via WhatsApp</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            Need help? Call us at <a href="tel:+94778590241" className="text-pink-600 hover:text-pink-800">+94 77 859 0241</a>
          </p>
        </div>
      </div>
    </div>
  );
} 